import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Clock, Users, CalendarCheck, Smile, UserCheck } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useFamily } from "../context/FamilyContext";
import { Loading } from "../components/Bits";

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="hf-card p-5">
    <Icon className="h-6 w-6 text-hf-coral" />
    <p className="mt-3 font-heading text-3xl font-semibold">{value ?? "-"}</p>
    <p className="text-base text-hf-muted">{label}</p>
  </div>
);

export default function Partner() {
  const { base, isDemo } = useFamily();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get("/partner/dashboard").then((r) => setD(r.data)).catch((e) => setErr(errMsg(e)));
  }, []);
  if (err) return <p data-testid="partner-error" className="text-lg">{err}</p>;
  if (!d) return <Loading />;
  const m = d.metrics;
  const summaries = d.families.filter((f) => f.last_summary);
  return (
    <div className="space-y-6">
      <div className="hf-rise">
        <p className="hf-eyebrow">Program partner view</p>
        <h1 className="mt-2 font-heading text-3xl font-semibold">{d.program}</h1>
        {isDemo && <p className="mt-2 text-base text-hf-muted">A fictional GUIDE program with 12 fictional families.</p>}
      </div>
      <div data-testid="partner-headline-metric" className="rounded-2xl bg-hf-green p-6 text-hf-cream">
        <p className="text-sm font-semibold uppercase tracking-widest text-hf-sun">Verified respite hours delivered this month</p>
        <p data-testid="partner-hours-value" className="mt-2 font-heading text-6xl font-semibold">{m.hours_month}</p>
        <p className="mt-1 text-lg text-hf-cream/80">Every hour has a start, an end, and an approximate location.</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Stat icon={Users} label="Enrolled families" value={m.enrolled} testid="partner-stat-enrolled" />
        <Stat icon={CalendarCheck} label="Visits this month" value={m.visits_month} testid="partner-stat-visits" />
        <Stat icon={Smile} label="Caregiver rest, before to after" value={m.rest_before ? `${m.rest_before} to ${m.rest_after}` : null} testid="partner-stat-rest" />
        <Stat icon={UserCheck} label="Helpers ready" value={m.helpers_ready} testid="partner-stat-helpers" />
      </div>
      <Link to={`${base}/export`} data-testid="partner-export-csv" className="hf-btn hf-btn-ghost w-full">
        <Download className="h-5 w-5" /> Export visit log (CSV)
      </Link>
      <section className="hf-card overflow-x-auto p-0" data-testid="partner-families-table">
        <h2 className="px-5 pt-5 font-heading text-xl font-semibold">Families</h2>
        <table className="mt-3 w-full min-w-[560px] text-left text-base">
          <thead className="bg-hf-cream text-sm uppercase tracking-wider text-hf-muted">
            <tr><th className="px-5 py-3">Family</th><th className="px-3">Stage</th><th className="px-3">Visits</th><th className="px-3">Hours</th><th className="px-3">Rest</th></tr>
          </thead>
          <tbody>
            {d.families.map((f) => (
              <tr key={f.id} data-testid={`partner-family-${f.id}`} className="border-t border-hf-line">
                <td className="px-5 py-3 font-semibold">{f.family}{f.is_live && <span className="ml-2 rounded-full bg-hf-coralsoft px-2 py-0.5 text-xs text-hf-coraldark">Live</span>}</td>
                <td className="px-3">{f.stage}</td>
                <td className="px-3">{f.visits_month}</td>
                <td className="px-3">{f.hours_month}</td>
                <td className="px-3">{f.rest_before && f.rest_after ? `${f.rest_before} to ${f.rest_after}` : "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      <section className="hf-card" data-testid="partner-summaries">
        <h2 className="font-heading text-xl font-semibold">Shift report summaries</h2>
        <p className="text-base text-hf-muted">Shared by families who opted in.</p>
        <ul className="mt-4 space-y-3">
          {summaries.map((f) => (
            <li key={f.id} className="rounded-xl bg-hf-cream p-4">
              <p className="text-sm font-semibold text-hf-muted">{f.family} · {f.helper.name}</p>
              <p className="mt-1 text-lg">{f.last_summary}</p>
            </li>
          ))}
        </ul>
      </section>
      <section className="hf-card" data-testid="partner-helper-roster">
        <h2 className="font-heading text-xl font-semibold">Helper roster</h2>
        <ul className="mt-3 divide-y divide-hf-line">
          {d.families.filter((f) => f.helper?.name).map((f) => (
            <li key={f.id} className="flex items-center justify-between py-3">
              <div><p className="text-lg font-semibold">{f.helper.name}</p><p className="text-base text-hf-muted">{f.helper.relationship} of the {f.family}</p></div>
              <span className={`rounded-full px-3 py-1 text-sm font-semibold ${f.helper.status === "Ready" ? "bg-hf-sage text-hf-green" : "bg-hf-sun/30 text-hf-ink"}`}>{f.helper.status}</span>
            </li>
          ))}
        </ul>
      </section>
      <p className="flex items-center gap-2 text-base text-hf-muted"><Clock className="h-4 w-4" /> Updated live as visits end.</p>
    </div>
  );
}
